import {
  SubscriptionPlanPrice,
  AdminFriendlySubscriptionPlan,
  AdminFriendlySubscriptionPlanDetailed,
} from 'api/subscription';
import { keyBy } from 'lodash-es';
import { SelectedPlans, SeatsCountMap } from 'types/subscriptions';

export const sortAndFilterPlans = (plans: SubscriptionPlanPrice[]): SubscriptionPlanPrice[] => {
  return plans
    .filter((plan) => ['month', 'year'].includes(plan.Period.Interval))
    .sort((a, b) => (a.Period.Interval === 'month' ? -1 : 1));
};

export const mapPriceIntervalToTitle = (interval: string) => {
  switch (interval) {
    case 'month':
      return 'Monthly';
    case 'year':
      return 'Annual';
    default:
      return 'Default'; // fallback, not used
  }
};

export const mapPriceIntervalToSubtext = (interval: string) => {
  switch (interval) {
    case 'year':
      return 'Save 20%';
    default:
      return null;
  }
};

export const calculateStandardPrice = (seats, standardPlans) => {
  let totalCost = 0;
  const allPrices = standardPlans.reduce((acc, plan) => [...acc, ...plan.Prices], []);
  const priceLookup = keyBy(allPrices, 'Nickname');

  const soloPrice = priceLookup.solo?.BasePrice || 0;
  const teamPrice = priceLookup.team?.BasePrice || 0;
  const seatPrice = priceLookup.seat?.BasePrice || 0;

  if (seats <= SeatsCountMap[SelectedPlans.Solo]) {
    totalCost = soloPrice;
  } else if (seats === SeatsCountMap[SelectedPlans.Team]) {
    totalCost = teamPrice;
  } else if (seats > SeatsCountMap[SelectedPlans.Team]) {
    totalCost = teamPrice + Math.abs(seats - SeatsCountMap[SelectedPlans.Team]) * seatPrice;
  }

  return totalCost;
};

export const calculateClassicPrice = (seats, classicPlans) => {
  let totalCost = 0;
  const allPrices = classicPlans.reduce((acc, plan) => [...acc, ...plan.Prices], []);
  const priceLookup = keyBy(allPrices, 'Nickname');

  const proPrice = priceLookup.pro?.BasePrice || 0;
  const growPrice = priceLookup.grow?.BasePrice || 0;
  const seatPrice = priceLookup.seat?.BasePrice || 0;

  if (seats <= SeatsCountMap[SelectedPlans.Pro]) {
    totalCost = proPrice;
  } else if (seats <= SeatsCountMap[SelectedPlans.Grow]) {
    totalCost = growPrice;
  } else if (seats > SeatsCountMap[SelectedPlans.Grow]) {
    totalCost = growPrice + Math.abs(seats - SeatsCountMap[SelectedPlans.Grow]) * seatPrice;
  }

  return totalCost;
};

export const calculateLegacyPrice = (seats, prices, priceKey) => {
  let totalCost = 0;
  const priceDetails = prices.find((price) => price.PriceKey === priceKey);

  if (!priceDetails) {
    throw new Error(`No price found for price key: ${priceKey}`);
  }

  const { BasePrice, BasePriceUnitsIncluded, NextPerUnitPrice } = priceDetails;

  if (seats <= BasePriceUnitsIncluded) {
    totalCost = BasePrice;
  } else {
    totalCost = seats * NextPerUnitPrice;
  }

  return totalCost;
};

/**
 * Converts AdminFriendlySubscriptionPlanDetailed to AdminFriendlySubscriptionPlan
 * Mapping rules:
 * - Solo, Team (Legacy) => standard
 * - Pro, Grow, BAMx, JJ => classic
 * - Others remain the same
 */
export const convertDetailedPlanToSimplePlan = (
  detailedPlan: AdminFriendlySubscriptionPlanDetailed | string,
): AdminFriendlySubscriptionPlan => {
  switch (detailedPlan) {
    case AdminFriendlySubscriptionPlanDetailed.LegacySolo:
    case AdminFriendlySubscriptionPlanDetailed.LegacyTeam:
      return AdminFriendlySubscriptionPlan.Standard;

    case AdminFriendlySubscriptionPlanDetailed.Pro:
    case AdminFriendlySubscriptionPlanDetailed.Grow:
    case AdminFriendlySubscriptionPlanDetailed.BAMx:
    case AdminFriendlySubscriptionPlanDetailed.JJ:
      return AdminFriendlySubscriptionPlan.Classic;

    case AdminFriendlySubscriptionPlanDetailed.InImplementation:
      return AdminFriendlySubscriptionPlan.InImplementation;

    case AdminFriendlySubscriptionPlanDetailed.Complimentary:
      return AdminFriendlySubscriptionPlan.Complimentary;

    case AdminFriendlySubscriptionPlanDetailed.Scale:
    default:
      return AdminFriendlySubscriptionPlan.Paid;
  }
};

/**
 * Converts AdminFriendlySubscriptionPlanDetailed to update plan parameters
 * with appropriate plan type, seat count, and identifier
 *
 * Examples:
 * - Pro => {plan: Classic, requestedSeatsCount: 2}
 * - BAMx => {plan: Classic, requestedSeatsCount: 2, identifier: 'BAMx'}
 * - Grow => {plan: Classic, requestedSeatsCount: 10}
 * - Solo (Legacy) => {plan: Standard, requestedSeatsCount: 2}
 * - Team (Legacy) => {plan: Standard, requestedSeatsCount: 3}
 */
export const convertDetailedPlanToUpdateParams = (
  detailedPlan: AdminFriendlySubscriptionPlanDetailed | string,
): {
  plan?: AdminFriendlySubscriptionPlan;
  requestedSeatsCount?: number;
  identifier?: string;
} => {
  switch (detailedPlan) {
    case AdminFriendlySubscriptionPlanDetailed.LegacySolo:
      return {
        plan: AdminFriendlySubscriptionPlan.Standard,
        requestedSeatsCount: 2,
      };

    case AdminFriendlySubscriptionPlanDetailed.LegacyTeam:
      return {
        plan: AdminFriendlySubscriptionPlan.Standard,
        requestedSeatsCount: 3,
      };

    case AdminFriendlySubscriptionPlanDetailed.Pro:
      return {
        plan: AdminFriendlySubscriptionPlan.Classic,
        requestedSeatsCount: 2,
      };

    case AdminFriendlySubscriptionPlanDetailed.Grow:
      return {
        plan: AdminFriendlySubscriptionPlan.Classic,
        requestedSeatsCount: 10,
      };

    case AdminFriendlySubscriptionPlanDetailed.BAMx:
      return {
        plan: AdminFriendlySubscriptionPlan.Classic,
        requestedSeatsCount: 2,
        identifier: AdminFriendlySubscriptionPlanDetailed.BAMx,
      };

    case AdminFriendlySubscriptionPlanDetailed.JJ:
      return {
        plan: AdminFriendlySubscriptionPlan.Classic,
        requestedSeatsCount: 2,
        identifier: AdminFriendlySubscriptionPlanDetailed.JJ,
      };

    case AdminFriendlySubscriptionPlanDetailed.Scale:
      return {
        plan: AdminFriendlySubscriptionPlan.Scale,
      };

    case AdminFriendlySubscriptionPlanDetailed.Complimentary:
      return {
        plan: AdminFriendlySubscriptionPlan.Complimentary,
      };

    default:
      return {};
  }
};
